import React from 'react';
import { LocationCard } from '@/components';
import { Vehicle } from '@/services/api/vehicles';

interface VehicleTelemetrySectionProps {
  vehicle: Vehicle;
}

const ID_NUMBER = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 });

/** Timestamp in the browser's timezone, e.g. "02 Sep 2026, 14:05". */
const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** How long ago the fix was taken, e.g. "3h ago". */
const formatAge = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const minutes = Math.max(
    0,
    Math.round((Date.now() - parsed.getTime()) / 60000)
  );
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

/**
 * Telemetry numbers arrive as raw vendor strings, so anything non-numeric is
 * shown as-is rather than swallowed by a failed parse.
 */
const formatMeasure = (
  value?: string | number | null,
  unit = '',
  { suffixSpace = true } = {}
): string => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  const formatted = ID_NUMBER.format(numeric);
  if (!unit) return formatted;
  return `${formatted}${suffixSpace ? ' ' : ''}${unit}`;
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-xs text-gray-400 mb-1">{label}</div>
    <div className="text-sm font-semibold text-gray-900">{children}</div>
  </div>
);

/**
 * "Latest Telemetry" band inside the Vehicle Details card — the snapshot the
 * unit last reported (telemetry_* columns). Every value stays '-' until
 * telemetry arrives.
 */
const VehicleTelemetrySection: React.FC<VehicleTelemetrySectionProps> = ({
  vehicle,
}) => {
  const lastAt = vehicle.telemetryLastAt ?? null;
  const age = formatAge(lastAt);
  const ignition = vehicle.telemetryIgnition;

  return (
    <>
      <div className="bg-gray-50 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        Latest Telemetry
      </div>

      <div className="divide-y divide-gray-100">
        {/* Last Update */}
        <div className="px-5 py-3">
          <Field label="Last Update">
            {lastAt ? (
              <>
                {formatDateTime(lastAt)}
                {age && (
                  <span className="ml-1.5 font-normal text-gray-400">
                    ({age})
                  </span>
                )}
              </>
            ) : (
              <span className="text-gray-400">No telemetry yet</span>
            )}
          </Field>
        </div>

        {/* Speed / Ignition */}
        <div className="grid grid-cols-2 gap-4 px-5 py-3">
          <Field label="Speed">
            {formatMeasure(vehicle.telemetrySpeed, 'km/h')}
          </Field>
          <Field label="Ignition">
            {ignition === null || ignition === undefined ? (
              '-'
            ) : (
              <span className={ignition ? 'text-[#1FC16B]' : 'text-gray-500'}>
                {ignition ? 'On' : 'Off'}
              </span>
            )}
          </Field>
        </div>

        {/* BMS 1 SOC / BMS 2 SOC */}
        <div className="grid grid-cols-2 gap-4 px-5 py-3">
          <Field label="BMS 1 SOC">
            {formatMeasure(vehicle.telemetryBms1Soc, '%', {
              suffixSpace: false,
            })}
          </Field>
          <Field label="BMS 2 SOC">
            {formatMeasure(vehicle.telemetryBms2Soc, '%', {
              suffixSpace: false,
            })}
          </Field>
        </div>

        {/* Odometer / Range */}
        <div className="grid grid-cols-2 gap-4 px-5 py-3">
          <Field label="Odometer">
            {formatMeasure(vehicle.telemetryOdometer, 'km')}
          </Field>
          <Field label="Range">
            {formatMeasure(vehicle.telemetryRange, 'km')}
          </Field>
        </div>

        {/* Last Known Location */}
        <div className="px-5 py-3">
          <LocationCard
            variant="plain"
            latitude={vehicle.telemetryLatitude ?? null}
            longitude={vehicle.telemetryLongitude ?? null}
            emptyMessage="No location data for this vehicle."
          />
        </div>
      </div>
    </>
  );
};

export default VehicleTelemetrySection;
